import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import * as authApi from '../api/auth'
import { readToken, setSessionExpiredHandler, writeToken } from '../api/client'
import type { Operator } from '../api/contracts'
import { AuthContext, type AuthState } from './authContext'

const OPERATOR_KEY = 'ps.operator'
const DEVICE_KEY = 'ps.deviceId'

function readStoredOperator(): Operator | null {
  try {
    const raw = localStorage.getItem(OPERATOR_KEY)
    return raw ? (JSON.parse(raw) as Operator) : null
  } catch {
    return null
  }
}

function readStoredDeviceId(): string {
  try {
    const stored = localStorage.getItem(DEVICE_KEY)
    if (stored) {
      return stored
    }
  } catch {
    // Fall through to the default below.
  }
  return 'HANDHELD-07'
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [operator, setOperator] = useState<Operator | null>(readStoredOperator)
  const [hasToken, setHasToken] = useState(() => readToken() !== null)
  const [sessionExpired, setSessionExpired] = useState(false)
  const [deviceId, setDeviceIdState] = useState(readStoredDeviceId)

  /**
   * The single place a dead session is handled. It clears the JWT and drops back to
   * login, and deliberately does NOT touch the outbox — queued scans must survive
   * re-login and flush afterwards.
   */
  useEffect(() => {
    setSessionExpiredHandler(() => {
      writeToken(null)
      setHasToken(false)
      setSessionExpired(true)
    })

    return () => setSessionExpiredHandler(null)
  }, [])

  const signIn = useCallback(
    async (username: string, password: string) => {
      const response = await authApi.login(username, password, deviceId)

      writeToken(response.accessToken)
      setHasToken(true)
      setOperator(response.operator)
      setSessionExpired(false)

      try {
        localStorage.setItem(OPERATOR_KEY, JSON.stringify(response.operator))
      } catch {
        // The operator name simply will not survive a reload.
      }
    },
    [deviceId],
  )

  const signOut = useCallback(async () => {
    try {
      await authApi.logout()
    } catch {
      // Signing out locally matters more than telling the server about it.
    }

    writeToken(null)
    setHasToken(false)
    setOperator(null)
    setSessionExpired(false)

    try {
      localStorage.removeItem(OPERATOR_KEY)
    } catch {
      // Nothing to do.
    }
  }, [])

  const setDeviceId = useCallback((next: string) => {
    setDeviceIdState(next)
    try {
      localStorage.setItem(DEVICE_KEY, next)
    } catch {
      // Nothing to do.
    }
  }, [])

  const value = useMemo<AuthState>(
    () => ({
      operator,
      deviceId,
      isAuthenticated: hasToken,
      sessionExpired,
      signIn,
      signOut,
      setDeviceId,
    }),
    [operator, deviceId, hasToken, sessionExpired, signIn, signOut, setDeviceId],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
